/**
 * Which detail screen a **disclosure** row drills down to, resolved from the API's `feature`.
 *
 * Disclosures do not all render the same way. Most hydrate into the generic name/description/status
 * table on `/disclosure-detail`; appointments carry five appointment-specific columns and get their
 * own screen. A third group — every kind of medication read — is logged without a hydratable record
 * set at all, so it drills down to nothing and its row is rendered inert.
 *
 * Admin-action rows are a different ledger with no `feature` at all and their own screen, so
 * callers must not run them through this (see `ActivityLogs.toRow`).
 */

/** The appointment detail screen (PBI 56795). */
const APPOINTMENT_DETAIL_ROUTE = '/appointment-detail';

/** The generic disclosed-records screen every other hydratable feature uses. */
const DISCLOSURE_DETAIL_ROUTE = '/disclosure-detail';

/**
 * The admin-action ledger's only detail screen. Declared here with the disclosure routes so all
 * three drill-down destinations are named in one place, even though this one isn't resolved from a
 * `feature` — admin actions don't have one. Adding a route here means adding it to
 * `AuditLogFilterStateService.isAuditLogUrl` and `App.auditLogSectionActive` too.
 */
export const ADMIN_ACTION_DETAIL_ROUTE = '/admin-action-detail';

/**
 * Feature keyword for the appointment family. The admin API registers a record provider for both
 * appointment source tables (native scheduling and Meditech), so these rows hydrate and open the
 * appointment detail screen.
 *
 * RISK — same shape as the deny-list below, matched on the same vocabulary this repo doesn't own.
 * A feature whose display name merely *contains* the word ("Appointment Summary" as a document,
 * an appointment-reminder message) would open the appointment screen, which renders only records
 * carrying an `appointment` payload. Mitigated rather than cured: that empty state links on to
 * `/disclosure-detail/{logId}`, which can show exactly those records (see
 * `AppointmentDetail.fallbackDetailRoute`), so a mismatch costs a click rather than the trail.
 * Widening this keyword is still not a safe edit, and the durable fix is the same server-supplied
 * route hint Bug 57117 should bring.
 */
const APPOINTMENT_FEATURE_KEYWORD = 'appointment';

/**
 * Feature keywords whose disclosure rows still have no drill-down. Every kind of medication read
 * (medication list, medication detail, refill requests, prescriptions) is logged without a
 * hydratable record set — the admin API has no provider for `PatientMedication`,
 * `PatientMedicationRefill` or `PatientMedicationPrescription` — so their detail screen would open
 * on an empty table. The row is rendered inert instead of promising a page with nothing on it.
 *
 * TEMPORARY, and now half of what it was: appointments were removed from this list when their
 * providers landed. Bug 57117 removes the file and its callers once medications hydrate too; it is
 * not a reason to leave them broken.
 *
 * Matched as a case-insensitive substring of the API's `feature` so a new sibling in the family is
 * covered with no code change.
 *
 * RISK — this is a deny-list matched on a vocabulary this repo does not own (the writers spell it,
 * in `AuditTableNames` in the Patients and Scheduling APIs). A feature whose display name merely
 * *contains* one of these words — "Medication Allergies" — would silently lose its drill-down: no
 * error, no telemetry, just a row that stops working on an audit surface. Broadening a keyword here
 * is never a safe edit. The durable fix is a server-supplied `hasDetail` flag on
 * `AuditEventSummary` — hydratability is a server-side fact — which is what Bug 57117 should
 * replace this with.
 */
const FEATURES_WITHOUT_DETAIL = ['medication'];

/**
 * The route a disclosure row opens, or `null` when it opens nothing.
 *
 * Drives both the row's click/keyboard activation and the affordances that advertise it (tab stop,
 * pointer cursor, link-styled patient name), so a non-navigable row never looks clickable.
 */
export function resolveDisclosureDetailRoute(feature: string): string | null {
  // `feature` is non-nullable on the model, but this renders whatever the API actually sends: a
  // missing value has to fall through to the generic screen, not throw on a table full of PHI.
  const normalized = (feature ?? '').toLowerCase();

  // Deliberately ahead of the deny-list below: the appointment providers exist, so an appointment
  // feature hydrates and must keep its drill-down even if a future deny-list keyword were to also
  // match it. Latent today — no keyword overlaps — but the precedence is a decision, not an
  // accident of ordering, and the two checks are otherwise identical in shape.
  if (normalized.includes(APPOINTMENT_FEATURE_KEYWORD)) {
    return APPOINTMENT_DETAIL_ROUTE;
  }

  if (FEATURES_WITHOUT_DETAIL.some((keyword) => normalized.includes(keyword))) {
    return null;
  }

  return DISCLOSURE_DETAIL_ROUTE;
}
